import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema, type UserCard } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ensureUserCardRows } from "@-/dev.improve.haniverse.lib.cards.user-card.rows";

/**
 * Return the actor `user_card` row for a grapheme's meaning lesson, creating a
 * non-study row when missing (e.g. favorites / collections). `skippedAt` applies only to a card
 * this call brought into existence; a card that was already there keeps its state.
 */
export async function ensureUserGraphemeMeaningCard(
  actorDb: ActorDatabase,
  grapheme: string,
  options?: { skippedAt?: string | null },
): Promise<UserCard> {
  // The common case writes nothing: the card is already there.
  const meaningRow = await actorDb(import.meta.location).query.userGraphemeMeaningCard.findFirst({
    where: { grapheme },
    with: { userCard: true },
  });
  const existing = meaningRow?.userCard;
  if (existing !== undefined) {
    return existing;
  }

  return actorDb(import.meta.location).transaction(async (tx) => {
    const nowIso = new Date().toISOString();
    const { ids, userCard } = await ensureUserCardRows(tx, { grapheme, kind: "meaning" }, nowIso);
    const skippedAt = options?.skippedAt ?? null;
    if (userCard !== "kept" && skippedAt !== null) {
      await tx
        .update(actorSchema.userCard)
        .set({ skippedAt })
        .where(eq(actorSchema.userCard.id, ids.userCardId));
    }
    const card = await tx.query.userCard.findFirst({ where: { id: ids.userCardId } });
    if (card === undefined) {
      throw new Error(`Failed to create meaning actor card for '${grapheme}'`);
    }
    return card;
  });
}
